// Screen 8 (spec 13 §Screens, §Imports; design spec §9 Wizard): the CSV import wizard, managers
// and operators. Five kinds, as the API takes them: time entries and costs (core), external
// identities (core: the id mappings the time importer resolves through) and metric values
// (observations, `kpi`: absent while the add-on is off, read-only in its grace period); tasks
// (core, M2-08) add a board and a column mapping, previewed in the browser (task-import.tsx;
// a board or mapping change discards the dry run too). Upload a CSV → dry run (`dry_run=true`: every row validated, nothing written) → the per-row table →
// commit. Commit stays disabled until the dry run of exactly this file is clean (no `error` or
// `unmapped` row); changing the file or the kind discards the dry run. Every upload carries
// immutable variables (pick id, kind, the file's byte snapshot, request id) and a response for
// a superseded pick or request is dropped (S1-17 review #4). Templates download as CSV;
// committed batches are listed with "Reverse" (agency operators, AlertDialog first,
// `POST /imports/{batch}/reverse`). One call on load: the batch list.
import { previewCsv, type TaskImportMapping } from "@companyos/domain-work/import";
import {
  Badge,
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  NativeSelect,
  NativeSelectOption,
  Notice,
  PageHeader,
  SectionHeading,
  SettingsSection,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  UploadZone,
  Wizard,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ListChecksIcon, UploadIcon } from "lucide-react";
import { type ChangeEvent, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { ImportBatch, ImportResult } from "../../api/types";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { importsQuery, keys } from "../queries";
import { Loading, LoadMore } from "../screen-parts";
import { ConfirmButton, ErrorNotice } from "../structure/parts";
import { useFeature } from "../use-feature";
import { workKeys } from "../work/work-queries";
import { type CsvPreview, TASKS_TEMPLATE, TaskImportPanel, taskSetupReady } from "./task-import";

export type ImportKind =
  | "time-entries"
  | "costs"
  | "external-identities"
  | "observations"
  | "tasks";

/** Columns per 13 §Imports (services/api/src/imports/*), with one example row. */
export const IMPORT_TEMPLATES: Record<
  ImportKind,
  { label: string; header: string; example: string; hint: string }
> = {
  "time-entries": {
    label: "Time entries",
    header: "work_date,grain,seat,person,kind,minutes,approval_status,note",
    example:
      "2026-10-12,day,Recruiting coordinator (DE),jordan.lee@acme.example,supervision,45,approved,Reviewed drafts",
    hint: "Hours per seat and person: a seat by title or id, a person by email or id.",
  },
  costs: {
    label: "Costs",
    header: "occurred_at,seat,kind,amount,currency,meter_identity,visibility,note",
    example:
      "2026-10-12T15:00:00Z,Recruiting coordinator (DE),adjustment,-20.00,USD,manual:credit-2026-10,client,Goodwill credit",
    hint: "Cost rows per seat; a credit is a negative adjustment.",
  },
  "external-identities": {
    label: "External identities",
    header: "system,kind,external_id,membership,seat,client_ref,valid_from",
    example: "clockify,person,5f1e2a,jordan.lee@acme.example,,,2026-10-01",
    hint: "Maps ids from another system (a time tracker, a CRM) to people, seats, DEs or clients.",
  },
  observations: {
    label: "Metric values",
    header: "metric_key,subject_kind,subject,period_key,value,note",
    example: "sales.calls_made,company,company,2026-W42,38,From the dialer export",
    hint: "Values of entered metrics, per company or seat and period; computed metrics are refused.",
  },
  // M2-08: core; the board and the column mapping are chosen below the file.
  tasks: TASKS_TEMPLATE,
};

const CLEAN = new Set(["ok", "duplicate", "revised"]);
const STATUS_LABELS: Record<string, string> = {
  ok: "OK",
  duplicate: "Duplicate",
  revised: "Revision",
  unmapped: "Unmapped",
  error: "Error",
};
const STATUS_TONE: Record<string, "success" | "neutral" | "info" | "warning" | "danger"> = {
  ok: "success",
  duplicate: "neutral",
  revised: "info",
  unmapped: "warning",
  error: "danger",
};

const templateHref = (k: ImportKind) =>
  `data:text/csv;charset=utf-8,${encodeURIComponent(
    `${IMPORT_TEMPLATES[k].header}\n${IMPORT_TEMPLATES[k].example}\n`,
  )}`;

/**
 * One chosen file, frozen when picked (review #4): its bytes are read once into an immutable
 * snapshot with a fresh id, so the dry run and the commit send exactly the same bytes, and a
 * re-pick of a same-named, same-sized file is still a different upload.
 */
type Picked = { id: number; file: File };
/** The variables of one upload: the immutable resource, file and request ids. */
type UploadVars = {
  pickId: number;
  kind: ImportKind;
  file: File;
  requestId: number;
  /** Tasks only: the board and the mapping (as JSON, also the dry run's identity). */
  board: string;
  mapping: string;
};

let nextId = 0;
const freshId = () => ++nextId;

async function snapshot(f: File): Promise<File> {
  const bytes = await f.arrayBuffer();
  return new File([bytes], f.name, { type: f.type || "text/csv", lastModified: f.lastModified });
}

export function ImportWizard({
  api,
  company,
  initialKind = "time-entries",
  initialBoard = "",
}: {
  api: Api;
  company: CompanyAccess;
  initialKind?: ImportKind;
  initialBoard?: string;
}) {
  const qc = useQueryClient();
  const kpi = useFeature("kpi");
  const operator = company.roles.includes("agency_operator");
  const kinds = (Object.keys(IMPORT_TEMPLATES) as ImportKind[]).filter(
    (k) => k !== "observations" || kpi.enabled,
  );
  const [kind, setKind] = useState<ImportKind>(initialKind);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [dry, setDry] = useState<{
    pickId: number;
    kind: ImportKind;
    file: File;
    /** Tasks: the board and mapping the dry run checked (a change discards it). */
    setup: string;
    result: ImportResult;
  } | null>(null);
  const [taskBoard, setTaskBoard] = useState(initialBoard);
  const [mapping, setMapping] = useState<TaskImportMapping | null>(null);
  const [preview, setPreview] = useState<CsvPreview | null>(null);
  const tasks = kind === "tasks";
  const setup = tasks ? JSON.stringify([taskBoard, mapping]) : "";
  const [committed, setCommitted] = useState<ImportResult | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // The newest pick and request: a response for anything older is superseded and dropped.
  const latestPick = useRef(0);
  const latestRequest = useRef(0);
  // Metric values are a `kpi` write: in the grace period the API refuses them.
  const frozen = kind === "observations" && kpi.readOnly;

  const upload = (v: UploadVars, dryRun: boolean) => {
    const form = new FormData();
    form.append("file", v.file, v.file.name);
    form.append("dry_run", dryRun ? "true" : "false");
    const input = { params: { co: company.id }, body: form };
    switch (v.kind) {
      case "tasks":
        form.append("mapping", v.mapping);
        return api.client.tasks.import({ params: { co: company.id, id: v.board }, body: form });
      case "costs":
        return api.client.costs.import(input);
      case "external-identities":
        return api.client.externalIdentities.import(input);
      case "observations":
        return api.client.observations.import(input);
      default:
        return api.client.timeEntries.import(input);
    }
  };
  const varsFor = (p: Picked, file: File = p.file): UploadVars => {
    latestRequest.current = freshId();
    return {
      pickId: p.id,
      kind,
      file,
      requestId: latestRequest.current,
      board: taskBoard,
      mapping: JSON.stringify(mapping),
    };
  };
  const setupOf = (v: UploadVars) =>
    v.kind === "tasks" ? JSON.stringify([v.board, JSON.parse(v.mapping)]) : "";
  const isCurrent = (v: UploadVars) =>
    v.pickId === latestPick.current && v.requestId === latestRequest.current;

  const dryRun = useMutation({
    mutationFn: (v: UploadVars) => upload(v, true),
    onSuccess: (result, v) => {
      if (isCurrent(v))
        setDry({ pickId: v.pickId, kind: v.kind, file: v.file, setup: setupOf(v), result });
    },
  });
  const commit = useMutation({
    mutationFn: (v: UploadVars) => upload(v, false),
    onSuccess: (result, v) => {
      // The batch is written either way: refresh the lists, but only a current commit resets.
      void qc.invalidateQueries({ queryKey: keys.imports(company.id) });
      void qc.invalidateQueries({ queryKey: ["company", company.id, "time"] });
      void qc.invalidateQueries({ queryKey: keys.companyObservations(company.id) });
      if (v.kind === "tasks") void qc.invalidateQueries({ queryKey: workKeys.all(company.id) });
      if (!isCurrent(v)) return;
      setCommitted(result);
      setDry(null);
      setPicked(null);
      latestPick.current = freshId();
      if (fileInput.current) fileInput.current.value = "";
    },
  });

  const current =
    dry && picked && dry.pickId === picked.id && dry.kind === kind && dry.setup === setup
      ? dry.result
      : null;
  const ready = !tasks || taskSetupReady(taskBoard, mapping);
  const errors = current?.rows.filter((r) => !CLEAN.has(r.status)).length ?? 0;
  const clean = !!current && current.rows.length > 0 && errors === 0;
  const busy = dryRun.isPending || commit.isPending;
  const step = committed ? 3 : clean ? 2 : current || picked ? 1 : 0;

  const choose = (p: Picked | null) => {
    latestPick.current = p ? p.id : freshId();
    setCommitted(null);
    setDry(null);
    setPicked(p);
  };
  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return choose(null);
    const id = freshId();
    latestPick.current = id; // anything in flight for the previous file is now superseded
    setDry(null);
    setPicked(null);
    void snapshot(f).then(async (file) => {
      // The header and first rows, for the tasks mapping and preview (read in the browser).
      const head = previewCsv(await file.text());
      if (latestPick.current !== id) return;
      setPreview(head);
      choose({ id, file });
    });
  };
  const onKind = (k: ImportKind) => {
    setKind(k);
    // Another resource is another upload: in-flight responses are superseded, no dry run.
    latestRequest.current = freshId();
    setDry(null);
    setCommitted(null);
  };

  return (
    <>
      <PageHeader
        eyebrow="Setup · Data"
        title="Import CSV"
        subtitle="Upload a CSV, check every row with a dry run, then commit the batch. A committed batch can be reversed by your CompanyOS operator."
      />
      <Wizard
        aria-label="Import steps"
        current={step}
        steps={[
          { label: "Upload", hint: "Choose what and a CSV file" },
          { label: "Review", hint: "Dry run, row by row" },
          { label: "Commit", hint: "Write the batch" },
        ]}
        footer={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <span
              id="import-commit-why"
              role="status"
              className="mr-auto text-sm text-muted-foreground"
            >
              {frozen
                ? "Metric values are read-only during the KPI add-on's grace period."
                : !picked
                  ? "Choose a CSV file."
                  : !ready
                    ? "Choose a board and the column with the task titles."
                    : !current
                      ? "Run a dry run of this file first."
                      : errors > 0
                        ? `${errors} row${errors === 1 ? "" : "s"} to fix before committing.`
                        : `${current.rows.length} row${current.rows.length === 1 ? "" : "s"} ready to commit.`}
            </span>
            <Button
              size="lg"
              variant="secondary"
              disabled={!picked || busy || frozen || !ready}
              onClick={() => picked && dryRun.mutate(varsFor(picked))}
            >
              {dryRun.isPending ? "Checking…" : "Dry run"}
            </Button>
            <Button
              size="lg"
              disabled={!clean || busy || frozen}
              onClick={() =>
                // Commit the exact bytes the clean dry run checked.
                picked && dry && commit.mutate(varsFor(picked, dry.file))
              }
              aria-describedby="import-commit-why"
            >
              {commit.isPending ? "Committing…" : "Commit"}
            </Button>
          </div>
        }
      >
        <SettingsSection icon={UploadIcon} title="Upload" description={IMPORT_TEMPLATES[kind].hint}>
          <div className="grid gap-5 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)]">
            <Field>
              <FieldLabel htmlFor="import-kind">What</FieldLabel>
              <NativeSelect
                id="import-kind"
                value={kind}
                onChange={(e) => onKind(e.target.value as ImportKind)}
              >
                {kinds.map((k) => (
                  <NativeSelectOption key={k} value={k}>
                    {IMPORT_TEMPLATES[k].label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <FieldDescription>
                <a
                  href={templateHref(kind)}
                  download={`${kind}-template.csv`}
                  className="underline underline-offset-4"
                >
                  Download the {IMPORT_TEMPLATES[kind].label.toLowerCase()} template
                </a>
              </FieldDescription>
              <FieldDescription className="font-mono text-2xs break-all">
                {IMPORT_TEMPLATES[kind].header}
              </FieldDescription>
            </Field>
            <div className="flex min-w-0 flex-col gap-2">
              <UploadZone
                ref={fileInput}
                aria-label="CSV file"
                accept=".csv,text/csv"
                title={picked ? picked.file.name : "Drop a CSV here or browse"}
                hint={
                  picked
                    ? `${Math.max(1, Math.round(picked.file.size / 1024))} KB · choose another to replace it`
                    : "Up to 5000 rows · .csv"
                }
                onChange={onFile}
              />
            </div>
          </div>
          {tasks ? (
            <TaskImportPanel
              api={api}
              co={company.id}
              preview={picked ? preview : null}
              board={taskBoard}
              onBoard={setTaskBoard}
              mapping={mapping}
              onMapping={setMapping}
            />
          ) : null}
        </SettingsSection>

        {committed ? (
          <Notice tone="success" title="Imported" className="mt-6" data-slot="import-committed">
            {committed.rows.length} row{committed.rows.length === 1 ? "" : "s"} committed as batch{" "}
            <span className="font-mono">…{committed.batch_id.slice(-8)}</span>.
          </Notice>
        ) : null}

        {current ? (
          <SettingsSection
            icon={ListChecksIcon}
            title="Dry run"
            description="Nothing has been written yet."
            className="mt-6"
          >
            <DryRunTable result={current} />
          </SettingsSection>
        ) : null}
      </Wizard>

      <section aria-labelledby="import-batches" className="mt-10 flex min-w-0 flex-col">
        <SectionHeading headingId="import-batches" title="Committed batches" />
        <Batches api={api} company={company} operator={operator} />
      </section>
    </>
  );
}

function DryRunTable({ result }: { result: ImportResult }) {
  return (
    <Table data-slot="dry-run">
      <TableCaption>
        One row per data row of the file (the header is row 0).{" "}
        {Object.entries(result.counts)
          .map(([s, n]) => `${STATUS_LABELS[s] ?? s}: ${n}`)
          .join(" · ")}
      </TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col" numeric>
            Row
          </TableHead>
          <TableHead scope="col">Status</TableHead>
          <TableHead scope="col">Problems</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {result.rows.map((r) => (
          <TableRow key={r.row} data-row={r.row} data-status={r.status}>
            <TableHead scope="row" className="text-right font-normal tabular-nums">
              {r.row}
            </TableHead>
            <TableCell>
              <Badge variant={STATUS_TONE[r.status] ?? "neutral"}>
                {STATUS_LABELS[r.status] ?? r.status}
              </Badge>
            </TableCell>
            <TableCell>
              {r.errors?.length ? (
                <ul className="flex flex-col gap-0.5 text-sm">
                  {r.errors.map((e) => (
                    <li key={`${e.column ?? ""}:${e.code}`}>
                      {e.column ? <span className="font-mono text-xs">{e.column}</span> : null}
                      {e.column ? ": " : ""}
                      {e.message}
                    </li>
                  ))}
                </ul>
              ) : (
                "—"
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Batches({
  api,
  company,
  operator,
}: {
  api: Api;
  company: CompanyAccess;
  operator: boolean;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const batches = useInfiniteQuery(importsQuery(api, co));
  const reverse = useMutation({
    mutationFn: (batch: string) => api.client.imports.reverse({ params: { co, batch } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.imports(co) });
      void qc.invalidateQueries({ queryKey: ["company", co, "time"] });
      void qc.invalidateQueries({ queryKey: keys.companyObservations(co) });
    },
  });
  if (batches.isPending) return <Loading label="Loading import batches" rows={2} />;
  if (batches.isError) return <ErrorNotice what="Import batches" error={batches.error} />;
  const rows: ImportBatch[] = batches.data.pages.flatMap((p) => p.data);
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Table data-slot="import-batches">
        <TableCaption>
          Committed import batches, newest first.
          {operator ? "" : " Reversing a batch is done by your CompanyOS operator."}
        </TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Batch</TableHead>
            <TableHead scope="col">What</TableHead>
            <TableHead scope="col" numeric>
              Rows
            </TableHead>
            <TableHead scope="col">Committed</TableHead>
            <TableHead scope="col">State</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length ? (
            rows.map((b) => {
              const what = IMPORT_TEMPLATES[b.resource as ImportKind]?.label ?? b.resource;
              return (
                <TableRow key={b.batch_id} data-batch={b.batch_id}>
                  <TableHead
                    scope="row"
                    className="font-mono text-xs font-normal"
                    title={b.batch_id}
                  >
                    …{b.batch_id.slice(-8)}
                  </TableHead>
                  <TableCell>{what}</TableCell>
                  <TableCell numeric>{b.rows}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(b.committed_at)}
                  </TableCell>
                  <TableCell>
                    {b.reversed_at ? (
                      <Badge variant="outline">Reversed</Badge>
                    ) : operator ? (
                      <ConfirmButton
                        label="Reverse"
                        srLabel={`batch …${b.batch_id.slice(-8)}`}
                        title={`Reverse this ${what.toLowerCase()} batch?`}
                        description={
                          b.resource === "tasks"
                            ? `The tasks its ${b.rows} row${b.rows === 1 ? "" : "s"} created are deleted, with any edits made since. A batch is reversed once.`
                            : `Its ${b.rows} row${b.rows === 1 ? "" : "s"} are undone with new rows (voided, tombstoned, reversed or closed); nothing is deleted. A batch is reversed once.`
                        }
                        confirmLabel="Reverse batch"
                        pending={reverse.isPending}
                        onConfirm={() => reverse.mutateAsync(b.batch_id)}
                      />
                    ) : (
                      <Badge variant="success">Committed</Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground">
                No imports yet. Committed batches appear here.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <LoadMore
        what="batches"
        hasMore={batches.hasNextPage}
        loading={batches.isFetchingNextPage}
        onMore={() => void batches.fetchNextPage()}
      />
    </div>
  );
}
